"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { ArrowRight, ChevronRight, Sparkles } from "lucide-react";

import { MegaMenuItem } from "@/lib/services/services";

interface MegaMenuProps {
  isOpen: boolean;
  items: MegaMenuItem[];
  onClose: () => void;
  dict?: MegaMenuDict;
}

export interface MegaMenuDict {
  mega_menu_label?: string;
  mega_menu_description?: string;
  mega_menu_links_suffix?: string;
  mega_menu_services_suffix?: string;
  mega_menu_direct_services?: string;
  mega_menu_selected_description?: string;
  mega_menu_direct_description?: string;
  mega_menu_go_to_page?: string;
  mega_menu_open_detail?: string;
  mega_menu_empty_title?: string;
  mega_menu_empty_description?: string;
}

export default function MegaMenu({ isOpen, items, onClose, dict }: MegaMenuProps) {
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(
    items[0]?.id ?? null
  );
  const [activeSubcategoryId, setActiveSubcategoryId] = useState<number | null>(null);

  // Gecersiz/bos secimler render sirasinda ilk elemana duser (effect ile
  // state senkronlamak yerine turetilir).
  const activeCategory = useMemo(
    () => items.find((item) => item.id === activeCategoryId) ?? items[0],
    [items, activeCategoryId]
  );

  const groupedChildren = useMemo(() => {
    const children = activeCategory?.children ?? [];

    return {
      subcategories: children.filter((item) => item.type === "category"),
      directServices: children.filter((item) => item.type === "service"),
    };
  }, [activeCategory]);

  const activeSubcategory = useMemo(() => {
    const { subcategories } = groupedChildren;
    if (subcategories.length === 0) {
      return null;
    }

    return (
      (activeSubcategoryId
        ? subcategories.find((item) => item.id === activeSubcategoryId)
        : undefined) ?? subcategories[0]
    );
  }, [activeSubcategoryId, groupedChildren]);

  // Kategori degisince alt kategori secimi sifirlanir; boylece yeni kategorinin
  // ilk alt kategorisi secili gelir (onceki effect davranisiyla ayni).
  const selectCategory = (categoryId: number) => {
    if (categoryId !== activeCategory?.id) {
      setActiveSubcategoryId(null);
    }
    setActiveCategoryId(categoryId);
  };

  const visibleServices = activeSubcategory?.children ?? groupedChildren.directServices;
  const hasSubcategories = groupedChildren.subcategories.length > 0;

  if (!items.length) return null;

  if (!isOpen) return null;

  // Acilis animasyonu CSS ile (tw-animate-css); framer-motion her sayfaya
  // ~130 KB JS ekliyordu.
  return (
        <div
          className="absolute left-1/2 top-full z-50 w-[96%] max-w-[1240px] -translate-x-1/2 px-4 pt-3"
          onMouseLeave={onClose}
        >
          <div
            className="animate-in fade-in-0 slide-in-from-bottom-3 zoom-in-[0.992] duration-200 ease-out overflow-hidden rounded-[24px] border border-[#dbe9f5] bg-white shadow-[0_24px_72px_-28px_rgba(5,30,60,0.28)]"
          >
            <div
              className={`grid min-h-[430px] ${
                hasSubcategories
                  ? "grid-cols-[220px_250px_minmax(0,1fr)]"
                  : "grid-cols-[220px_minmax(0,1fr)]"
              }`}
            >
              <div className="border-r border-[#e6eef5] bg-[linear-gradient(180deg,#f7fbff_0%,#eef6fc_100%)] p-3">
                <div className="rounded-[18px] border border-[#d9e8f4] bg-white/80 p-3.5">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#5d85a8]">
                    {dict?.mega_menu_label}
                  </div>
                  <div className="mt-1.5 text-[13px] leading-5 text-slate-600">
                    {dict?.mega_menu_description}
                  </div>
                </div>

                <div className="mt-3 max-h-[336px] space-y-1 overflow-y-auto pr-1">
                  {items.map((category) => {
                    const isActive = category.id === activeCategory?.id;
                    const childCount = category.children?.length ?? 0;

                    return (
                      <button
                        key={category.id}
                        type="button"
                        onMouseEnter={() => selectCategory(category.id)}
                        onFocus={() => selectCategory(category.id)}
                        onClick={() => selectCategory(category.id)}
                        className={`flex w-full items-center justify-between rounded-[18px] px-3.5 py-2.5 text-left transition-all duration-200 ${
                          isActive
                            ? "bg-[#0f4c81] text-white shadow-[0_12px_30px_rgba(15,76,129,0.2)]"
                            : "text-[#0f4c81] hover:bg-white hover:shadow-sm"
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold">{category.title}</div>
                          <div
                            className={`mt-0.5 text-[11px] ${
                              isActive ? "text-blue-100/80" : "text-slate-500"
                            }`}
                          >
                            {childCount} {dict?.mega_menu_links_suffix}
                          </div>
                        </div>
                        <ChevronRight
                          className={`h-4 w-4 shrink-0 transition-transform ${
                            isActive ? "translate-x-0.5 text-white" : "text-slate-400"
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>

              {hasSubcategories && activeCategory && (
                <div className="border-r border-[#e8eef4] bg-[#fbfdff] p-4">
                  

                  <div className="mt-4 space-y-2">
                    {groupedChildren.subcategories.map((subcategory) => {
                      const isActive = subcategory.id === activeSubcategory?.id;
                      const serviceCount = subcategory.children?.length ?? 0;

                      return (
                        <button
                          key={subcategory.id}
                          type="button"
                          onMouseEnter={() => setActiveSubcategoryId(subcategory.id)}
                          onFocus={() => setActiveSubcategoryId(subcategory.id)}
                          onClick={() => setActiveSubcategoryId(subcategory.id)}
                          className={`w-full rounded-[18px] border px-3.5 py-3 text-left transition-all ${
                            isActive
                              ? "border-[#cfe3f5] bg-white shadow-[0_10px_24px_rgba(15,76,129,0.08)]"
                              : "border-transparent bg-transparent hover:border-[#e1ebf4] hover:bg-white"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="truncate text-sm font-semibold text-[#0f4c81]">
                                {subcategory.title}
                              </div>
                              <div className="mt-1 text-[12px] text-slate-500">
                                {serviceCount} {dict?.mega_menu_services_suffix}
                              </div>
                            </div>
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {groupedChildren.directServices.length > 0 && (
                    <div className="mt-5 rounded-[18px] border border-[#e4edf5] bg-white p-3.5">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5d85a8]">
                        {dict?.mega_menu_direct_services}
                      </div>
                      <div className="mt-3 space-y-2">
                        {groupedChildren.directServices.slice(0, 4).map((service) => (
                          <Link
                            key={service.id}
                            href={service.link}
                            onClick={onClose}
                            className="flex items-center justify-between rounded-[14px] bg-[#f8fbfe] px-3 py-2 text-[13px] font-semibold text-[#0f4c81] transition-colors hover:text-[#00AEEF]"
                          >
                            <span className="pr-3">{service.title}</span>
                            <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-[radial-gradient(circle_at_top_right,_rgba(0,174,239,0.12),_transparent_28%),linear-gradient(180deg,#ffffff_0%,#fbfdff_100%)] p-5">
                {activeCategory && (
                  <div className="flex h-full flex-col">
                    <div className="flex items-start justify-between gap-5 border-b border-[#e8eef4] pb-4">
                      <div className="max-w-2xl">
                        <h3 className="text-[1.9rem] font-bold tracking-[-0.04em] text-[#051e3c]">
                          {activeSubcategory?.title || activeCategory.title}
                        </h3>
                        <p className="mt-1.5 text-[13px] leading-5 text-slate-600">
                          {hasSubcategories
                            ? dict?.mega_menu_selected_description
                            : dict?.mega_menu_direct_description}
                        </p>
                      </div>

                      <Link
                        href={(activeSubcategory || activeCategory).link}
                        onClick={onClose}
                        className="inline-flex shrink-0 items-center rounded-xl border border-[#d7e6f2] bg-white px-4 py-2.5 text-sm font-semibold text-[#0f4c81] transition-colors hover:border-[#0f4c81]/20 hover:text-[#00AEEF]"
                      >
                        {dict?.mega_menu_go_to_page}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </div>

                    <div className="mt-5 min-h-0 flex-1 overflow-y-auto pr-1">
                      {visibleServices.length > 0 ? (
                        <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
                          {visibleServices.map((service) => (
                            <Link
                              key={service.id}
                              href={service.link}
                              onClick={onClose}
                              className="group rounded-[18px] border border-[#e4edf5] bg-white p-4 shadow-[0_10px_24px_rgba(15,76,129,0.05)] transition-all hover:-translate-y-0.5 hover:border-[#cfe3f5] hover:shadow-[0_16px_30px_rgba(15,76,129,0.1)]"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <div className="text-[14px] font-bold leading-5 text-[#0f4c81] transition-colors group-hover:text-[#00AEEF]">
                                    {service.title}
                                  </div>
                                  <div className="mt-2 text-[12px] text-slate-500">
                                    {dict?.mega_menu_open_detail}
                                  </div>
                                </div>
                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#eef7ff] text-[#0f4c81] transition-colors group-hover:bg-[#0f4c81] group-hover:text-white">
                                  <ArrowRight className="h-4 w-4" />
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <div className="flex h-full items-center justify-center rounded-[20px] border border-dashed border-[#d8e8f4] bg-white/75 p-7 text-center">
                          <div>
                            <div className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#eef7ff] text-[#0f4c81]">
                              <Sparkles className="h-5 w-5" />
                            </div>
                            <div className="mt-3 text-base font-semibold text-[#051e3c]">
                              {dict?.mega_menu_empty_title}
                            </div>
                            <div className="mt-2 text-[13px] leading-5 text-slate-500">
                              {dict?.mega_menu_empty_description}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
  );
}
